// Converted from shared/design/components/SectionTabs.html by scripts/dc2tsx; edited by hand from here on.
import React from 'react';
import { DCLogic } from '../ds/DCLogic';
import { each, ip, route, sx } from '../ds/tpl';
import { t } from '../i18n';
import { LiveNavCtx } from '../ds/liveNav';

export interface SectionTabsProps {
  section?: 'exposure' | 'accounts' | 'toxic' | 'cases' | 'alerts' | 'revenue' | 'reports' | 'ai' | (string & {});
  active?: string;
  demo?: any;
  /** live counts per tab id, e.g. { coordinated: 3 } (alerts and approvals come from the shell) */
  counts?: Record<string, number>;
  [key: string]: any;
}

/** Permission of each section tab (default: the section's page permission). */
const TAB_PERM: Record<string, Record<string, string>> = {
  exposure: { overview: 'page.exposure', symbol: 'page.exposure', var: 'page.exposure', ab: 'page.ab_book' },
  accounts: { accounts: 'page.accounts', insights: 'page.accounts', groups: 'page.accounts' },
  toxic: { rules: 'page.detection_rules', _: 'page.toxic' },
  cases: { _: 'page.cases' },
  alerts: { alerts: 'page.alerts', rules: 'page.alert_rules' },
  revenue: { _: 'page.revenue' },
  reports: { _: 'page.reports' },
  ai: { _: 'page.ai' },
};

export class SectionTabs extends DCLogic<SectionTabsProps> {
  static contextType = LiveNavCtx;
  renderVals() {
    const p = this.props;
    const S: any = {
      exposure: [
        t('c.sectiontabs.sec-exposure', 'Exposure and risk'),
        [
          ['overview', t('c.sectiontabs.exposure-overview', 'Overview'), 'EXP-01_exposure-overview'],
          ['symbol', t('c.sectiontabs.exposure-symbol', 'Symbol detail'), 'EXP-02_exposure-symbol'],
          ['var', t('c.sectiontabs.exposure-var', 'VaR and stress tests'), 'EXP-03_var-stress'],
          ['ab', t('c.sectiontabs.exposure-ab', 'A-Book / B-Book'), 'EXP-04_ab-book-summary'],
        ],
      ],
      accounts: [
        t('c.sectiontabs.sec-accounts', 'Accounts and groups'),
        [
          ['accounts', t('c.sectiontabs.accounts-accounts', 'Accounts'), 'ACC-01_accounts-list'],
          ['insights', t('c.sectiontabs.accounts-insights', 'Insights'), 'ACC-02_accounts-insights'],
          ['groups', t('c.sectiontabs.accounts-groups', 'Groups'), 'ACC-03_groups-list'],
        ],
      ],
      toxic: [
        t('c.sectiontabs.sec-toxic', 'Toxic flow'),
        [
          ['overview', t('c.sectiontabs.toxic-overview', 'Overview'), 'TOX-01_toxic-overview'],
          ['markout', t('c.sectiontabs.toxic-markout', 'Markout'), 'TOX-02_markout'],
          ['coordinated', t('c.sectiontabs.toxic-coordinated', 'Coordinated groups'), 'TOX-03_coordinated-groups', 3, 'var(--crit)'],
          ['news', t('c.sectiontabs.toxic-news', 'News trading'), 'TOX-05_news-trading'],
          ['bonus', t('c.sectiontabs.toxic-bonus', 'Bonus and credit'), 'TOX-06_bonus-credit'],
          ['swapfree', t('c.sectiontabs.toxic-swapfree', 'Swap-free'), 'TOX-07_swap-free'],
          ['access', t('c.sectiontabs.toxic-access', 'Access intelligence'), 'TOX-08_access-intelligence'],
          ['rules', t('c.sectiontabs.toxic-rules', 'Detection rules'), 'TOX-09_detection-rules'],
        ],
      ],
      cases: [
        t('c.sectiontabs.sec-cases', 'Cases'),
        [
          ['cases', t('c.sectiontabs.cases-cases', 'Cases'), 'CAS-01_cases-list'],
          ['approvals', t('c.sectiontabs.cases-approvals', 'Pending approvals'), 'CAS-05_approvals', 2, 'var(--warn)'],
        ],
      ],
      alerts: [
        t('c.sectiontabs.sec-alerts', 'Alerts'),
        [
          ['alerts', t('c.sectiontabs.alerts-alerts', 'Alerts'), 'ALR-01_alerts', 5, 'var(--crit)'],
          ['rules', t('c.sectiontabs.alerts-rules', 'Alert rules'), 'ALR-03_alert-rules'],
        ],
      ],
      revenue: [
        t('c.sectiontabs.sec-revenue', 'Revenue'),
        [
          ['overview', t('c.sectiontabs.revenue-overview', 'Overview'), 'REV-01_revenue-overview'],
          ['breakdown', t('c.sectiontabs.revenue-breakdown', 'Breakdown'), 'REV-02_revenue-breakdown'],
          ['swap', t('c.sectiontabs.revenue-swap', 'Swap reconciliation'), 'REV-03_swap-reconciliation'],
        ],
      ],
      reports: [
        t('c.sectiontabs.sec-reports', 'Reports'),
        [
          ['library', t('c.sectiontabs.reports-library', 'Library'), 'RPT-01_reports-library'],
          ['scheduled', t('c.sectiontabs.reports-scheduled', 'Scheduled'), 'RPT-03_scheduled-reports'],
        ],
      ],
      ai: [
        t('c.sectiontabs.sec-ai', 'Ask Brainux'),
        [
          ['ask', t('c.sectiontabs.ai-ask', 'Ask Brainux'), 'AI-01_ask-brainux'],
          ['brief', t('c.sectiontabs.ai-brief', 'Daily brief'), 'AI-02_daily-brief'],
        ],
      ],
    };
    const sectionKey = p.section ?? 'exposure';
    const sec = S[sectionKey] || S.exposure;
    const active = p.active ?? sec[1][0][0];
    const live = this.context as import('../ds/liveNav').LiveNav | null;
    let items = sec[1];
    if (live) {
      // live counts replace the design's samples; tabs follow the session's permissions
      const liveN: Record<string, number | undefined> = {
        'cases:approvals': live.counts.approvals_pending,
        'alerts:alerts': live.counts.alerts_open,
        ...(p.counts || {}),
      };
      const perms = TAB_PERM[sectionKey] || {};
      items = items
        .filter(([id]) => {
          const perm = perms[id] || perms._;
          return !perm || live.permissions.includes(perm);
        })
        .map(([id, label, file, , nc]) => [id, label, file, liveN[sectionKey + ':' + id] ?? liveN[id] ?? 0, nc]);
    }
    return {
      wrap: p.demo ? 'padding: 16px; background: var(--bg);' : '',
      aria: t('c.sectiontabs.aria', '{section} sections', { section: sec[0] }),
      tabs: items.map(([id, label, file, n, nc]) => ({
        label,
        href: file + '.html',
        sel: id === active ? 'true' : 'false',
        cur: id === active ? 'page' : 'false',
        n: n || 0,
        nColor: nc || 'var(--muted)',
      })),
    };
  }
  view(v: any) {
    return (
      <div style={sx(`font-family: var(--font-sans); color: var(--ink); ${v.wrap ?? ''}`)}>
        <nav className="bx-tabs" aria-label={v.aria}>
          {each(v.tabs, (tab, $tI) => (
            <a key={$tI} className="bx-tab" href={route(tab?.href)} aria-current={tab?.cur}>
              {ip(tab?.label)}
              {tab?.n ? (
                <span className="bx-tab-n" style={{ color: tab?.nColor }}>
                  {ip(tab?.n)}
                </span>
              ) : null}
            </a>
          ))}
        </nav>
      </div>
    );
  }
}
